import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useToast, ToastContainer } from "../../components/common/Toast";
import DeleteConfirmModal from "../../components/decks/DeleteConfirmModal";
import HtmlPreviewPane from "../../components/admin/HtmlPreviewPane";
import { useAuth } from "../../contexts/AuthContext";
import {
  newsletterAdminService,
  NewsletterEdition,
  NewsletterTopic,
  EditionStatus,
} from "../../services/newsletterAdminService";

const editionBadge = (status: EditionStatus) => {
  const styles: Record<EditionStatus, string> = {
    draft: "bg-gray-600/20 text-gray-300 border-gray-600/50",
    approved: "bg-blue-600/20 text-blue-300 border-blue-600/50",
    sending: "bg-yellow-600/20 text-yellow-300 border-yellow-600/50",
    sent: "bg-green-600/20 text-green-300 border-green-600/50",
    failed: "bg-red-600/20 text-red-300 border-red-600/50",
  };
  return (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status]}`}
    >
      {status.toUpperCase()}
    </span>
  );
};

const NewsletterEditionEditor: React.FC = () => {
  const { id, eid } = useParams<{ id: string; eid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  const [edition, setEdition] = useState<NewsletterEdition | null>(null);
  const [sourceTopic, setSourceTopic] = useState<NewsletterTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft fields — mirror edition when loaded, controlled locally while editing
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [notes, setNotes] = useState("");

  // Action states
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);

  // Branded preview (body + subject wrapped by the backend template). Debounced
  // so rapid typing doesn't flood the server with /render calls.
  const [renderedHtml, setRenderedHtml] = useState("");

  const load = useCallback(async () => {
    if (!id || !eid) return;
    try {
      setLoading(true);
      setError(null);
      const data = await newsletterAdminService.getEdition(id, eid);
      setEdition(data);
      setSubject(data.subject ?? "");
      setHtml(data.generated_html ?? "");
      setNotes(data.notes ?? "");
      if (data.topic_id) {
        try {
          const topics = await newsletterAdminService.listTopics(id);
          setSourceTopic(topics.find((t) => t.id === data.topic_id) ?? null);
        } catch {
          setSourceTopic(null);
        }
      } else {
        setSourceTopic(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load edition");
    } finally {
      setLoading(false);
    }
  }, [id, eid]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounced call to /render so the preview pane shows the branded email
  // (with header, dark theme, typography) rather than the raw body fragment.
  useEffect(() => {
    if (!html.trim()) {
      setRenderedHtml("");
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const wrapped = await newsletterAdminService.renderHtml(subject, html);
        if (!cancelled) setRenderedHtml(wrapped);
      } catch {
        // Fallback: if /render fails, preview the raw body. Avoids a blank pane.
        if (!cancelled) setRenderedHtml(html);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [subject, html]);

  const applyEdition = (next: NewsletterEdition) => {
    setEdition(next);
    setSubject(next.subject ?? "");
    setHtml(next.generated_html ?? "");
    setNotes(next.notes ?? "");
  };

  const handleSave = async () => {
    if (!id || !eid) return;
    try {
      setSaving(true);
      const updated = await newsletterAdminService.updateEdition(id, eid, subject, html);
      setEdition(updated);
      showSuccess("Draft saved");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!id || !eid) return;
    try {
      setRegenerating(true);
      const updated = await newsletterAdminService.regenerateEdition(
        id,
        eid,
        notes.trim() || undefined
      );
      applyEdition(updated);
      if (updated.generation_error) {
        showError("Generation failed", updated.generation_error);
      } else {
        showSuccess("Draft regenerated");
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const handleApprove = async () => {
    if (!id || !eid) return;
    try {
      setApproving(true);
      const updated = await newsletterAdminService.approveEdition(id, eid);
      setEdition(updated);
      showSuccess("Edition approved");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to approve edition");
    } finally {
      setApproving(false);
    }
  };

  const handleSendTest = async () => {
    if (!id || !eid) return;
    try {
      setSendingTest(true);
      const result = await newsletterAdminService.sendTestEdition(id, eid);
      if (result.success) {
        showSuccess(
          "Test sent",
          `Check ${user?.email ?? "your inbox"} for the test email.`
        );
      } else {
        showError("Test send failed", result.error ?? "Mailgun returned an error");
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async () => {
    if (!id || !eid) return;
    try {
      setSending(true);
      const summary = await newsletterAdminService.sendEdition(id, eid);
      showSuccess(
        "Newsletter sent",
        `Delivered to ${summary.successCount} of ${summary.totalContacts} recipients.`
      );
      setShowSendConfirm(false);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleResendFailed = async () => {
    if (!id || !eid) return;
    try {
      setResending(true);
      const result = await newsletterAdminService.resendFailedEdition(id, eid);
      showSuccess(
        "Resend complete",
        `Retried ${result.retried}, converted ${result.converted}. Total delivered: ${result.recipientCount}.`
      );
      setShowResendConfirm(false);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Loading edition...</p>
        </div>
      </div>
    );
  }

  if (error || !edition) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-6">
          <p className="text-red-300">{error ?? "Edition not found"}</p>
          <button
            onClick={() => navigate(id ? `/admin/newsletters/${id}` : "/admin/newsletters")}
            className="mt-4 px-4 py-2 text-purple-300 hover:text-purple-200"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const isDraft = edition.status === "draft";
  const isApproved = edition.status === "approved";
  const isFailed = edition.status === "failed";
  const isSent = edition.status === "sent";
  const hasContent = Boolean(edition.subject && edition.generated_html);
  const hasFailures =
    isSent &&
    typeof edition.recipient_count === "number" &&
    edition.recipient_count >= 0 &&
    !!edition.send_session_id;

  const inputClass =
    "w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50";

  return (
    <div className="max-w-7xl mx-auto">
      <button
        onClick={() => navigate(`/admin/newsletters/${id}`)}
        className="text-sm text-gray-400 hover:text-white mb-4"
      >
        ← Back to newsletter
      </button>

      <PageHeader
        title={`Edition #${edition.edition_number}`}
        subtitle={edition.subject || "Draft — no subject yet"}
        action={editionBadge(edition.status)}
      />

      {sourceTopic && (
        <div className="bg-purple-900/15 border border-purple-700/40 rounded-lg p-4 mb-6">
          <div className="text-xs text-purple-300/70 uppercase tracking-wide mb-1">
            Source topic
          </div>
          <div className="text-white text-sm font-semibold">{sourceTopic.title}</div>
          {sourceTopic.description && (
            <div className="text-purple-100/70 text-xs mt-1">
              {sourceTopic.description}
            </div>
          )}
        </div>
      )}

      {edition.generation_error && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4 mb-6">
          <div className="text-red-300 text-sm font-semibold mb-1">
            Last generation/send failed
          </div>
          <div className="text-red-200 text-xs font-mono">
            {edition.generation_error}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: controls */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 space-y-5">
          <div>
            <label htmlFor="ed-subject" className="block text-sm font-medium text-gray-300 mb-2">
              Subject
            </label>
            <input
              id="ed-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!isDraft}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="ed-html" className="block text-sm font-medium text-gray-300 mb-2">
              HTML body
            </label>
            <textarea
              id="ed-html"
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              disabled={!isDraft}
              rows={18}
              className={`${inputClass} font-mono text-xs`}
            />
          </div>

          <div>
            <label htmlFor="ed-notes" className="block text-sm font-medium text-gray-300 mb-2">
              Notes (used by regenerate)
            </label>
            <textarea
              id="ed-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isDraft}
              rows={4}
              placeholder="Talking points for the next regenerate..."
              className={inputClass}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-700">
            {isDraft && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || regenerating || !subject.trim() || !html.trim()}
                  className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 transition-all"
                >
                  {saving ? "Saving..." : "Save draft"}
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating || saving}
                  className="px-4 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 disabled:opacity-50 transition-all"
                >
                  {regenerating ? "Regenerating..." : "Regenerate"}
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving || !hasContent}
                  className="px-4 py-2 bg-blue-600/20 text-blue-300 border border-blue-600/50 rounded-lg text-sm font-semibold hover:bg-blue-600/30 disabled:opacity-50 transition-all"
                  title={!hasContent ? "Generate content before approving" : undefined}
                >
                  {approving ? "Approving..." : "Approve"}
                </button>
              </>
            )}

            {hasContent && (
              <button
                onClick={handleSendTest}
                disabled={sendingTest}
                className="px-4 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 disabled:opacity-50 transition-all"
                title={`Send to ${user?.email ?? "your own email"}`}
              >
                {sendingTest ? "Sending..." : "Send test to me"}
              </button>
            )}

            {isApproved && (
              <button
                onClick={() => setShowSendConfirm(true)}
                className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all"
              >
                Send to all users
              </button>
            )}

            {isFailed && hasContent && (
              <button
                onClick={() => setShowSendConfirm(true)}
                className="px-5 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg font-semibold hover:from-amber-700 hover:to-amber-800 transition-all"
                title="Retry the broadcast for this failed edition"
              >
                Retry send
              </button>
            )}

            {hasFailures && (
              <button
                onClick={() => setShowResendConfirm(true)}
                disabled={resending}
                className="px-4 py-2 bg-amber-600/20 text-amber-200 border border-amber-600/50 rounded-lg text-sm font-semibold hover:bg-amber-600/30 disabled:opacity-50 transition-all"
                title="Re-attempt delivery to recipients whose first send failed"
              >
                {resending ? "Resending..." : "Resend to failed recipients"}
              </button>
            )}
          </div>
        </div>

        {/* Right: preview */}
        <div>
          <div className="text-sm text-gray-400 mb-2">Preview</div>
          <HtmlPreviewPane html={renderedHtml} height="820px" />
        </div>
      </div>

      {showSendConfirm && (
        <DeleteConfirmModal
          title="Send newsletter to all users?"
          message="This will email every SendUpdates.ai user who has not opted out of marketing emails. This cannot be undone — make sure you've reviewed the content and sent yourself a test email."
          confirmLabel={sending ? "Sending..." : "Send now"}
          isDeleting={sending}
          onCancel={() => (sending ? undefined : setShowSendConfirm(false))}
          onConfirm={handleSend}
        />
      )}

      {showResendConfirm && (
        <DeleteConfirmModal
          title="Resend to failed recipients?"
          message="Re-attempts delivery to every recipient whose first send failed for this edition. Hard-bounce addresses will also be retried — only proceed if you've confirmed those addresses are good."
          confirmLabel={resending ? "Resending..." : "Resend now"}
          isDeleting={resending}
          onCancel={() => (resending ? undefined : setShowResendConfirm(false))}
          onConfirm={handleResendFailed}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default NewsletterEditionEditor;
